import type {ResultadoDeExcelDeFinanciaciones} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useCargarExcelDeFinanciaciones} from '../api/financiacion';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_FINANCIACIONES as A} from './ayuda-exportaciones';
import {ElegirArchivo} from './elegir-archivo';
import {NotaDeAyuda} from './nota-de-ayuda';

// Carga del Excel para trabajar las financiaciones: primero se revisa que
// cambiaria; nada se guarda hasta confirmar. Solo cuenta lo que se toco.
export function CargaExcelDeFinanciaciones({onCerrar}: {onCerrar: () => void}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoDeExcelDeFinanciaciones | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cargar = useCargarExcelDeFinanciaciones();

  const enviar = async (modo: 'revisar' | 'aplicar', elegido = archivo) => {
    if (!elegido) return;
    setError(null);
    try {
      setResultado(await cargar.mutateAsync({archivo: elegido, modo}));
    } catch (err) {
      setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo leer el archivo.', 'Could not read the file.'));
    }
  };
  const r = resultado;
  const porGuardar = r ? r.resumen.crear + r.resumen.editar : 0;
  const accion: Record<string, string> = {
    crear: tr('Nueva financiación', 'New financing'),
    editar: tr('Cambia', 'Changes'),
    conflicto: tr('Choque', 'Conflict'),
    error: tr('Error', 'Error'),
    sin_cambios: tr('Sin cambios', 'No changes'),
  };
  const conCambios = r?.filas.filter(f => f.accion !== 'sin_cambios') ?? [];

  return (
    <div
      className="space-y-3 rounded-lg border border-linea bg-white p-4 text-sm"
      data-prueba="carga-excel-financiaciones"
    >
      <p className="text-apagado">
        {tr(
          'Sube el Excel que descargaste con "⬇ Excel para trabajar", ya con tus cambios. La app revisa qué cambia y no guarda nada hasta que confirmes.',
          'Upload the Excel you downloaded with "⬇ Excel to work on", with your changes. The app reviews what changes and saves nothing until you confirm.',
        )}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <ElegirArchivo
          etiqueta={tr('Archivo de Excel', 'Excel file')}
          accept=".xlsx,.xlsm"
          archivos={archivo ? [archivo] : []}
          deshabilitado={cargar.isPending}
          onElegir={([elegido]) => {
            setArchivo(elegido ?? null);
            setResultado(null);
            if (elegido) void enviar('revisar', elegido);
          }}
        />
        <button type="button" onClick={onCerrar} className="ml-auto text-apagado hover:underline">
          {tr('Cerrar', 'Close')}
        </button>
      </div>
      {cargar.isPending && <p className="text-apagado">{tr('Revisando…', 'Reviewing…')}</p>}
      {error && (
        <p className="text-alerta" role="alert">
          {error}
        </p>
      )}
      {r && (
        <>
          <p className={r.aplicado ? 'rounded-md bg-verde-claro/60 p-2 font-medium text-verde-oscuro' : ''}>
            {r.aplicado
              ? tr(
                  `Listo: ${numero(r.resumen.crear)} financiaciones nuevas y ${numero(r.resumen.editar)} con cambios.`,
                  `Done: ${numero(r.resumen.crear)} new financings and ${numero(r.resumen.editar)} changed.`,
                )
              : tr(
                  `Nuevas: ${numero(r.resumen.crear)} · Cambian: ${numero(r.resumen.editar)} · Sin cambios: ${numero(r.resumen.sinCambios)} · Choques: ${numero(r.resumen.conflictos)} · Con errores: ${numero(r.resumen.errores)}`,
                  `New: ${numero(r.resumen.crear)} · Changing: ${numero(r.resumen.editar)} · Unchanged: ${numero(r.resumen.sinCambios)} · Conflicts: ${numero(r.resumen.conflictos)} · With errors: ${numero(r.resumen.errores)}`,
                )}
          </p>
          {r.avisos.map(a => (
            <p key={a} className="text-apagado">
              {a}
            </p>
          ))}
          {conCambios.length > 0 && (
            <div className="max-h-80 overflow-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs tracking-wide text-apagado uppercase">
                    <th className="py-1 pr-3 font-semibold">{tr('Fila', 'Row')}</th>
                    <th className="py-1 pr-3 font-semibold">{tr('Exportación', 'Export')}</th>
                    <th className="py-1 pr-3 font-semibold">
                      {tr('Qué pasa', 'What happens')}
                      <NotaDeAyuda texto={A.queCambiaExcel} />
                    </th>
                    <th className="py-1 font-semibold">{tr('Detalle', 'Detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conCambios.map(f => (
                    <tr key={f.fila} className="border-t border-linea/70 align-top">
                      <td className="py-1 pr-3 tabular-nums">{f.fila}</td>
                      <td className="py-1 pr-3">{f.exportacion ?? '—'}</td>
                      <td
                        className={`py-1 pr-3 ${f.accion === 'error' || f.accion === 'conflicto' ? 'text-alerta' : ''}`}
                      >
                        {accion[f.accion]}
                      </td>
                      <td className="py-1">
                        {f.mensaje && <div>{f.mensaje}</div>}
                        {f.cambios.map(c => (
                          <div key={c.campo} className="text-xs">
                            <span className="text-apagado">{c.etiqueta}:</span> {c.antes ?? '—'} →{' '}
                            <b>{c.despues ?? '—'}</b>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!r.aplicado && porGuardar > 0 && (
            <button
              type="button"
              disabled={cargar.isPending}
              onClick={() => void enviar('aplicar')}
              className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr(`Guardar ${numero(porGuardar)} cambios`, `Save ${numero(porGuardar)} changes`)}
            </button>
          )}
        </>
      )}
    </div>
  );
}
